import { useMemo } from 'react';

import { useClient } from 'urql';
import { fromPromise } from 'xstate';

import { ConflictType, OrderType } from '@sg/graphql-schema';

import { TelemetryCartHandoffBag } from '@order/Telemetry';

import { type CartHandoffMutation, CartHandoffDocument } from '../../graphql/CartHandoff.generated';
import {
  type HandoffCartParams,
  type HandoffCartResponse,
} from '../../machine/cart-handoff-machine.actors';

/**
 * Returns whether the handoff was successful, whether there are no items available, and the conflicts.
 */
export const useHandoffCart = () => {
  const client = useClient();

  const handoffCart = useMemo(
    () =>
      fromPromise<HandoffCartResponse, HandoffCartParams>(async ({ input }) => {
        const { orderToken, ignoreConflicts } = input;

        const handoffCartMutation = client.mutation<CartHandoffMutation>;

        const response = await handoffCartMutation(CartHandoffDocument, {
          orderToken,
          ignoreConflicts,
        }).toPromise();

        const payload = response.data?.handoffCart;

        const isSuccess = payload?.__typename === 'ReorderSuccess' && Boolean(payload.cart.id);
        const hasNothingAvailable = checkIfNothingAvailable(payload);
        const conflicts = getReorderConflicts(payload);
        const telemetryBag = payload?.__typename === 'ReorderSuccess' && payload.cart.id ? formatTelemetryBag(payload.cart) : null; // prettier-ignore

        return {
          isSuccess,
          hasNothingAvailable,
          conflicts,
          telemetryBag,
        };
      }),
    [client.mutation],
  );

  return { handoffCart };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getReorderConflicts(payload: CartHandoffMutation['handoffCart'] | undefined) {
  if (payload?.__typename === 'ReorderConflict') {
    return [...new Set(payload.conflicts.flatMap(classifyConflict))];
  }

  return [];
}

function classifyConflict(conflict: ReorderConflictPayload['conflicts'][number]) {
  if (conflict.type === ConflictType.IngredientUnavailable) {
    const ingredientNames = conflict.unavailableIngredients.map(({ name }) => name);
    return ingredientNames.length > 0 ? ingredientNames : [conflict.product.name];
  }

  if (conflict.type === ConflictType.ProductUnavailable) {
    return [conflict.product.name];
  }

  return [];
}

function checkIfNothingAvailable(payload: CartHandoffMutation['handoffCart'] | undefined) {
  if (payload?.__typename === 'ReorderSuccess') return false;

  return (
    !payload ||
    payload.__typename !== 'ReorderConflict' ||
    payload.isNothingAvailable ||
    payload.conflicts.length === 0
  );
}

function formatTelemetryBag(cart: ReorderSuccessCart): TelemetryCartHandoffBag {
  const { ledger, lineItems, restaurant } = cart;

  return {
    orderId: Number(cart.id),
    restaurantId: Number(restaurant.id),
    restaurantName: restaurant.name,
    orderType: getOrderType(cart),
    orderValue: getOrderValue(ledger),
    bagCount: lineItems.reduce((count, item) => count + item.quantity, 0),
    productNames: lineItems.map((item) => item.product.name),
  };
}

function getOrderType(cart: ReorderSuccessCart): TelemetryCartHandoffBag['orderType'] {
  if (cart.restaurant.isOutpost) return 'outpost';
  if (cart.orderType === OrderType.Delivery) return 'delivery';

  return 'pickup';
}

function getOrderValue(ledger: ReorderSuccessCart['ledger']) {
  const total =
    ledger.subtotal +
    ledger.tax +
    ledger.tip +
    ledger.feesTotal -
    ledger.discountsTotal -
    ledger.creditsTotal;

  return total / 100;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ReorderSuccessCart = Extract<
  CartHandoffMutation['handoffCart'],
  { __typename: 'ReorderSuccess' }
>['cart'];

type ReorderConflictPayload = Extract<
  CartHandoffMutation['handoffCart'],
  { __typename: 'ReorderConflict' }
>;
